import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../../../lib/utils';
import { useAppSettingsStore } from '../../../../stores/appSettingsStore';

/**
 * The Culinary Cloud brand mark, as the navbar uses it (`common/NavBar.tsx`): `/logo.png` in
 * light, `/app_logo.png` in dark. Both files are the MARK alone — a cloud-and-fork, no wordmark —
 * so no cropping is needed. The dark asset is a square navy tile, which the navbar rounds off
 * with `object-cover rounded-full`; this mirrors that exactly so the two never diverge.
 */
export const CloudMark: React.FC<{
  /** Rendered size in px — ~20 in the transcript, ~28 in the activity line's expanded state. */
  size?: number;
  /** Adds the "working" pulse: a soft halo behind plus a gentle scale on the mark. */
  pulsing?: boolean;
  className?: string;
}> = ({ size = 20, pulsing = false, className }) => {
  const darkMode = useAppSettingsStore((s) => s.darkMode);

  const img = (
    <img
      src={darkMode ? '/app_logo.png' : '/logo.png'}
      alt=""
      aria-hidden="true"
      width={size}
      height={size}
      style={{ width: size, height: size }}
      className={cn('shrink-0 rounded-full', darkMode ? 'object-cover' : 'object-contain')}
    />
  );

  if (!pulsing) return <span className={cn('inline-flex shrink-0', className)}>{img}</span>;

  return (
    <span
      className={cn('relative inline-flex items-center justify-center shrink-0', className)}
      style={{ width: size, height: size }}
    >
      <motion.span
        className="absolute rounded-full bg-darkGreenColor/25 dark:bg-darkGreenColor-dark/25"
        style={{ width: size, height: size }}
        animate={{ scale: [1, 1.55, 1], opacity: [0.55, 0, 0.55] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.span
        className="relative inline-flex"
        animate={{ scale: [1, 1.07, 1] }}
        transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
      >
        {img}
      </motion.span>
    </span>
  );
};

export default CloudMark;
